import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../supabase/client';
import { getProductById, getPriceIdForCurrency } from '../stripe-config';
import { Currency } from '../types';

const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY;

// Initialize Stripe with proper error handling
export const stripePromise = loadStripe(STRIPE_PUBLIC_KEY).catch(err => {
  console.error('Failed to initialize Stripe:', err);
  return null;
});

// Function to create a checkout session
export const createCheckoutSession = async (productId: string, currency: Currency) => {
  try {
    console.log('Creating checkout session for product:', productId, 'in currency:', currency);

    const product = getProductById(productId);

    if (!product) {
      throw new Error('Producto no encontrado');
    }

    const priceId = getPriceIdForCurrency(productId, currency);

    if (!priceId) {
      throw new Error('Price ID no encontrado para esta moneda');
    }

    console.log('Found product:', product, 'Price ID:', priceId);
    
    // Get the current user's session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Usuario no autenticado');
    }
    
    console.log('Frontend: Token de acceso de la sesión:', session.access_token);
    console.log('Frontend: Usuario ID:', session.user?.id);
    console.log('Frontend: Token expira en:', new Date(session.expires_at! * 1000));

    console.log('Calling Supabase Edge Function with data:', {
      price_id: priceId,
      mode: product.mode,
      currency: currency,
      success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/cancel`
    });

    const { data, error } = await supabase.functions.invoke('stripe-checkout', {
      body: {
        price_id: priceId,
        mode: product.mode,
        currency: currency,
        success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/cancel`
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });
    
    if (error) {
      console.error('Supabase Edge Function error:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw new Error(error.message || 'Error al crear sesión de checkout');
    }
    
    if (!data) {
      console.error('No data returned from Edge Function');
      throw new Error('No se recibió respuesta del servidor');
    }
    
    console.log('Checkout session created:', data);
    return data;
  } catch (error: any) {
    console.error('Error creating checkout session:', error);
    throw new Error(error.message || 'Error al crear sesión de checkout');
  }
};

// Function to get user's subscription status
export const getUserSubscription = async () => {
  try {
    const { data, error } = await supabase
      .from('stripe_user_subscriptions')
      .select('*')
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }
    
    return data;
  } catch (error: any) {
    console.error('Error getting user subscription:', error);
    return null;
  }
};

// Function to get user's order history
export const getUserOrders = async () => {
  try {
    const { data, error } = await supabase
      .from('stripe_user_orders')
      .select('*')
      .order('order_date', { ascending: false });
    
    if (error) {
      console.error('Error fetching orders:', error);
      return [];
    }
    
    return data || [];
  } catch (error: any) {
    console.error('Error getting user orders:', error);
    return [];
  }
};